import { idx_data_insert_variants } from "./idx_data_insert_variants";
import { idx_data_thumb_outer_variants } from "./idx_data_thumb_outer_variants";
import { idx_data_insert_types as fingerTypesSource } from "./idx_data_insert_types.source";
import { idx_data_thumb_insert_types as thumbTypesSource } from "./idx_data_thumb_insert_types.source";

let _byTypeSize = null;
let _sizesByType = null;
let _labelToId = null;
// Thumb-side indexes
let _thumbCategory = null; // typeId → "switch" | "simple"
let _thumbSimpleBySize = null; // typeId → Map(size → Map(color → upc))
let _thumbSimpleSizesByType = null; // typeId → Set(size)
let _thumbSwitchInnersBySize = null; // typeId → Map(inner_size → Map(color → upc))
let _thumbSwitchInnerSizesByType = null; // typeId → Set(inner_size)
let _thumbSwitchOuters = null; // typeId → Map(color → upc)

const norm = (s) => String(s || "").trim().toLowerCase();

const build = () => {
  if (_byTypeSize) return;
  _byTypeSize = new Map();
  _sizesByType = new Map();
  _labelToId = new Map();
  for (const entry of idx_data_insert_variants) {
    const { type_id, size, variants } = entry;
    if (!type_id || !size) continue;
    if (!_byTypeSize.has(type_id)) _byTypeSize.set(type_id, new Map());
    const sizeMap = _byTypeSize.get(type_id);
    const colorMap = new Map();
    for (const v of variants || []) {
      if (v?.color && v?.upc) colorMap.set(String(v.color).toUpperCase(), v.upc);
    }
    sizeMap.set(String(size), colorMap);
    if (!_sizesByType.has(type_id)) _sizesByType.set(type_id, new Set());
    _sizesByType.get(type_id).add(String(size));
  }
  // SpecSheet form stores grip_type / thm_insert_type as display labels
  // (e.g. "Turbo Quad Oval"), but idx_data_insert_variants is keyed by the
  // constant id ("INSERT_TURBO_QUAD_OVAL"). Build a label→id map so a
  // bare label or a bare constant both resolve.
  for (const entry of fingerTypesSource || []) {
    if (entry?.id) {
      _labelToId.set(norm(entry.type), entry.id);
      _labelToId.set(norm(entry.id), entry.id);
    }
  }
  for (const entry of thumbTypesSource || []) {
    if (entry?.id) {
      _labelToId.set(norm(entry.type), entry.id);
      _labelToId.set(norm(entry.id), entry.id);
    }
  }

  _thumbCategory = new Map();
  _thumbSimpleBySize = new Map();
  _thumbSimpleSizesByType = new Map();
  _thumbSwitchInnersBySize = new Map();
  _thumbSwitchInnerSizesByType = new Map();
  _thumbSwitchOuters = new Map();
  for (const entry of idx_data_thumb_outer_variants || []) {
    const { type_id, category } = entry;
    if (!type_id || !category) continue;
    _thumbCategory.set(type_id, category);
    if (category === "simple") {
      const size = entry.size;
      if (!size) continue;
      if (!_thumbSimpleBySize.has(type_id))
        _thumbSimpleBySize.set(type_id, new Map());
      const sizeMap = _thumbSimpleBySize.get(type_id);
      const colorMap = new Map();
      for (const v of entry.variants || []) {
        if (v?.color && v?.upc) colorMap.set(String(v.color).toUpperCase(), v.upc);
      }
      sizeMap.set(String(size), colorMap);
      if (!_thumbSimpleSizesByType.has(type_id))
        _thumbSimpleSizesByType.set(type_id, new Set());
      _thumbSimpleSizesByType.get(type_id).add(String(size));
    } else if (category === "switch") {
      const innerSize = entry.inner_size;
      if (!innerSize) continue;
      if (!_thumbSwitchInnersBySize.has(type_id))
        _thumbSwitchInnersBySize.set(type_id, new Map());
      const innerMap = _thumbSwitchInnersBySize.get(type_id);
      const innerColorMap = new Map();
      for (const v of entry.inner_variants || []) {
        if (v?.color && v?.upc)
          innerColorMap.set(String(v.color).toUpperCase(), v.upc);
      }
      innerMap.set(String(innerSize), innerColorMap);
      if (!_thumbSwitchInnerSizesByType.has(type_id))
        _thumbSwitchInnerSizesByType.set(type_id, new Set());
      _thumbSwitchInnerSizesByType.get(type_id).add(String(innerSize));
      // Outers are the same across sizes — merge on each size entry.
      if (!_thumbSwitchOuters.has(type_id))
        _thumbSwitchOuters.set(type_id, new Map());
      const outerMap = _thumbSwitchOuters.get(type_id);
      for (const v of entry.outer_variants || []) {
        if (v?.color && v?.upc)
          outerMap.set(String(v.color).toUpperCase(), v.upc);
      }
    }
  }
};

// Accept either the constant id ("INSERT_TURBO_QUAD_OVAL") or the display
// label as stored on a SpecSheet form ("Turbo Quad Oval"). Returns the
// canonical id or null.
const toTypeId = (input) => {
  if (!input) return null;
  build();
  return _labelToId.get(norm(input)) || null;
};

export const getAvailableSizes = (typeIdOrLabel) => {
  build();
  const typeId = toTypeId(typeIdOrLabel);
  if (!typeId) return [];
  const set = _sizesByType.get(typeId);
  return set ? Array.from(set) : [];
};

export const getAvailableColors = (typeIdOrLabel, size) => {
  build();
  const typeId = toTypeId(typeIdOrLabel);
  if (!typeId) return [];
  const sizeMap = _byTypeSize.get(typeId);
  if (!sizeMap) return [];
  const colorMap = sizeMap.get(String(size));
  if (!colorMap) return [];
  return Array.from(colorMap.keys());
};

export const resolveInsertUpc = (typeIdOrLabel, size, color) => {
  build();
  const typeId = toTypeId(typeIdOrLabel);
  if (!typeId) return null;
  const sizeMap = _byTypeSize.get(typeId);
  if (!sizeMap) return null;
  const colorMap = sizeMap.get(String(size));
  if (!colorMap) return null;
  return colorMap.get(String(color).toUpperCase()) || null;
};

export const hasInsertVariants = (typeIdOrLabel) => {
  build();
  const typeId = toTypeId(typeIdOrLabel);
  if (!typeId) return false;
  return _byTypeSize.has(typeId);
};

// ---------------------------------------------------------------------------
// Thumb-side lookups. The thumb fixture has two categories:
//   - "simple": one sized slug → 1 line item per pick
//   - "switch": sized inner sleeve + universal outer → 2 line items per pick
// SpecSheet thm_insert_type comes as a display label (e.g. "Turbo Switch
// Grip") so every entry point routes through toTypeId().
// ---------------------------------------------------------------------------

export const getThumbCategory = (typeIdOrLabel) => {
  build();
  const typeId = toTypeId(typeIdOrLabel);
  if (!typeId) return null;
  return _thumbCategory.get(typeId) || null;
};

export const hasThumbVariants = (typeIdOrLabel) => {
  build();
  const typeId = toTypeId(typeIdOrLabel);
  if (!typeId) return false;
  return _thumbCategory.has(typeId);
};

// Simple thumbs ---------------------------------------------------------------

export const getThumbSimpleSizes = (typeIdOrLabel) => {
  build();
  const typeId = toTypeId(typeIdOrLabel);
  if (!typeId) return [];
  const set = _thumbSimpleSizesByType.get(typeId);
  return set ? Array.from(set) : [];
};

export const getThumbSimpleColors = (typeIdOrLabel, size) => {
  build();
  const typeId = toTypeId(typeIdOrLabel);
  if (!typeId) return [];
  const sizeMap = _thumbSimpleBySize.get(typeId);
  if (!sizeMap) return [];
  const colorMap = sizeMap.get(String(size));
  if (!colorMap) return [];
  return Array.from(colorMap.keys());
};

export const resolveThumbSimpleUpc = (typeIdOrLabel, size, color) => {
  build();
  const typeId = toTypeId(typeIdOrLabel);
  if (!typeId) return null;
  const sizeMap = _thumbSimpleBySize.get(typeId);
  if (!sizeMap) return null;
  const colorMap = sizeMap.get(String(size));
  if (!colorMap) return null;
  return colorMap.get(String(color).toUpperCase()) || null;
};

// Switch thumbs --------------------------------------------------------------

export const getThumbSwitchInnerSizes = (typeIdOrLabel) => {
  build();
  const typeId = toTypeId(typeIdOrLabel);
  if (!typeId) return [];
  const set = _thumbSwitchInnerSizesByType.get(typeId);
  return set ? Array.from(set) : [];
};

export const getThumbSwitchInnerColors = (typeIdOrLabel, innerSize) => {
  build();
  const typeId = toTypeId(typeIdOrLabel);
  if (!typeId) return [];
  const innerMap = _thumbSwitchInnersBySize.get(typeId);
  if (!innerMap) return [];
  const colorMap = innerMap.get(String(innerSize));
  if (!colorMap) return [];
  return Array.from(colorMap.keys());
};

export const resolveThumbSwitchInnerUpc = (typeIdOrLabel, innerSize, color) => {
  build();
  const typeId = toTypeId(typeIdOrLabel);
  if (!typeId) return null;
  const innerMap = _thumbSwitchInnersBySize.get(typeId);
  if (!innerMap) return null;
  const colorMap = innerMap.get(String(innerSize));
  if (!colorMap) return null;
  return colorMap.get(String(color).toUpperCase()) || null;
};

export const getThumbSwitchOuterColors = (typeIdOrLabel) => {
  build();
  const typeId = toTypeId(typeIdOrLabel);
  if (!typeId) return [];
  const outerMap = _thumbSwitchOuters.get(typeId);
  if (!outerMap) return [];
  return Array.from(outerMap.keys());
};

export const resolveThumbSwitchOuterUpc = (typeIdOrLabel, color) => {
  build();
  const typeId = toTypeId(typeIdOrLabel);
  if (!typeId) return null;
  const outerMap = _thumbSwitchOuters.get(typeId);
  if (!outerMap) return null;
  return outerMap.get(String(color).toUpperCase()) || null;
};
